import {
  confirmObligationBatch,
  ObligationBatchConfirmationApiError,
  type ConfirmObligationBatchResponse,
  getFixedRules,
  previewObligationBatch,
  ObligationBatchPreviewApiError,
  type FixedRuleContract,
  type ObligationBatchPreviewResponse,
  type ObligationBatchPreviewRow,
} from '@urbanik/api-client';
import {
  Avatar,
  Button,
  CanonicalCard,
  CanonicalMetricCard,
  CanonicalMetricGrid,
  CanonicalNotice,
  ConfirmationDialog,
  DataTable,
  FormActions,
  IconButton,
  MoneyValue,
  PageSizeSelector,
  ProgressBar,
  Pagination,
  PersonCell,
  ResultsCounter,
  RowSelection,
  SelectField,
  StatePanel,
  StatusBadge,
  TextField,
  canonicalStates,
  type DataColumn,
  Tabs,
} from '@urbanik/ui';
import { CircleDollarSign, Eye, ListChecks, UserMinus, UserRoundSearch, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';

type ReportRow = ObligationBatchPreviewRow & Readonly<{ selected: boolean; reportReason: string }>;
const cents = (amount: string) => {
  const [whole, fraction] = amount.split('.');
  return BigInt(whole!) * 100n + BigInt(fraction!);
};
const decimal = (value: bigint) => `${value / 100n}.${(value % 100n).toString().padStart(2, '0')}`;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();

export function ObligationBatchPreview({ canConfirm = false }: { canConfirm?: boolean }) {
  const [rules, setRules] = useState<readonly FixedRuleContract[]>([]),
    [ruleId, setRuleId] = useState(''),
    [period, setPeriod] = useState(''),
    [loading, setLoading] = useState(true),
    [running, setRunning] = useState(false),
    [data, setData] = useState<ObligationBatchPreviewResponse | null>(null),
    [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set()),
    [filter, setFilter] = useState<'all' | 'included' | 'excluded'>('all'),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(15),
    [showGuidance, setShowGuidance] = useState(true),
    [notice, setNotice] = useState<{ tone: 'error' | 'success'; message: string } | null>(null),
    [confirmOpen, setConfirmOpen] = useState(false),
    [confirming, setConfirming] = useState(false),
    [idempotencyKey, setIdempotencyKey] = useState(''),
    [confirmation, setConfirmation] = useState<ConfirmObligationBatchResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getFixedRules(apiBase, controller.signal)
      .then((result) => setRules(result.items.filter((rule) => rule.isCurrent && !rule.deletedAt)))
      .catch((error) => {
        if (!controller.signal.aborted)
          setNotice({
            tone: 'error',
            message: error instanceof Error ? error.message : 'No pudimos consultar las reglas.',
          });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const reportRows = useMemo<readonly ReportRow[]>(
    () =>
      data?.rows.map((row) => ({
        ...row,
        selected: row.included && selectedIds.has(row.id),
        reportReason:
          row.included && !selectedIds.has(row.id)
            ? 'Excluido manualmente por el operador.'
            : row.reason,
      })) ?? [],
    [data, selectedIds],
  );
  const includedRows = reportRows.filter((row) => row.selected);
  const excludedRows = reportRows.filter((row) => !row.selected);
  const manualExclusions = reportRows.filter((row) => row.included && !row.selected).length;
  const filteredRows = reportRows.filter(
    (row) => filter === 'all' || (filter === 'included' ? row.selected : !row.selected),
  );
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const rows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalAmount = decimal(includedRows.reduce((sum, row) => sum + cents(row.amount), 0n));
  const setSelected = (row: ObligationBatchPreviewRow, checked: boolean) => {
    if (!row.included) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(row.id);
      else next.delete(row.id);
      return next;
    });
  };
  const columns: readonly DataColumn<ReportRow>[] = [
    {
      id: 'selection',
      header: 'Generar',
      accessor: (row) => (row.selected ? 'Seleccionado' : 'Excluido'),
      cell: (row) => (
        <RowSelection
          checked={row.selected}
          disabled={!row.included}
          label={`${row.selected ? 'Excluir' : 'Incluir'} a ${row.memberName}`}
          onChange={(checked) => setSelected(row, checked)}
        />
      ),
    },
    {
      id: 'member',
      header: 'Socio',
      accessor: (row) => row.memberName,
      cell: (row) => (
        <PersonCell
          avatar={<Avatar initials={initials(row.memberName)} size="small" />}
          name={row.memberName}
          secondary={row.slotNumber ? `Cupo ${row.slotNumber}` : 'Sin cupo asignado'}
        />
      ),
    },
    { id: 'nationalId', header: 'Cédula', accessor: (row) => row.nationalId || '—' },
    { id: 'slot', header: 'Cupo', accessor: (row) => row.slotNumber ?? '—' },
    {
      id: 'result',
      header: 'Resultado',
      accessor: (row) => (row.selected ? 'Incluido' : 'Excluido'),
      cell: (row) => (
        <StatusBadge tone={row.selected ? 'success' : 'neutral'}>
          {row.selected ? 'Incluido' : 'Excluido'}
        </StatusBadge>
      ),
    },
    {
      id: 'amount',
      header: 'Importe',
      accessor: (row) => `$${row.selected ? row.amount : '0.00'}`,
      cell: (row) => (
        <MoneyValue
          value={row.selected ? row.amount : '0.00'}
          tone={row.selected ? 'positive' : 'neutral'}
        />
      ),
    },
    { id: 'reason', header: 'Razón / reporte', accessor: (row) => row.reportReason },
    {
      id: 'action',
      header: 'Acción',
      accessor: (row) => (row.included ? 'Ver socio' : 'Revisar socio'),
      cell: (row) => (
        <IconButton
          label={`${row.included ? 'Ver' : 'Revisar'} socio ${row.memberName}`}
          onClick={() => (location.hash = `#socios/${row.memberId}`)}
        >
          <UserRoundSearch aria-hidden="true" />
        </IconButton>
      ),
    },
  ];

  const run = async () => {
    setPage(1);
    setRunning(true);
    setData(null);
    try {
      const result = await previewObligationBatch(apiBase, { ruleId, period });
      setData(result);
      setSelectedIds(new Set(result.rows.filter((row) => row.included).map((row) => row.id)));
      setFilter('all');
      setIdempotencyKey(crypto.randomUUID());
      setConfirmation(null);
      setNotice({ tone: 'success', message: 'Vista previa calculada sin crear obligaciones.' });
    } catch (error) {
      setNotice({
        tone: 'error',
        message:
          error instanceof ObligationBatchPreviewApiError
            ? error.message
            : 'No pudimos calcular la vista previa.',
      });
    } finally {
      setRunning(false);
    }
  };
  if (loading)
    return <StatePanel state={canonicalStates.find((state) => state.kind === 'loading')!} />;
  const confirm = async () => {
    if (!data || !idempotencyKey || !includedRows.length) return;
    setConfirming(true);
    try {
      const result = await confirmObligationBatch(apiBase, {
        idempotencyKey,
        ruleId: data.rule.id,
        period: data.period,
        selectedSlotIds: includedRows.map((row) => row.slotId),
        manualExclusions: reportRows
          .filter((row) => row.included && !row.selected)
          .map((row) => ({ slotId: row.slotId, memberId: row.memberId, reason: row.reportReason })),
      });
      setConfirmation(result);
      setConfirmOpen(false);
      setNotice({
        tone: 'success',
        message: result.idempotentReplay
          ? 'El lote ya había sido procesado; se recuperó el mismo resumen sin duplicar obligaciones.'
          : 'Lote confirmado, generado y auditado correctamente.',
      });
    } catch (error) {
      setConfirmOpen(false);
      setNotice({
        tone: 'error',
        message:
          error instanceof ObligationBatchConfirmationApiError
            ? error.message
            : 'No pudimos confirmar el lote.',
      });
    } finally {
      setConfirming(false);
    }
  };
  return (
    <section aria-label="Vista previa masiva">
      <CanonicalNotice
        message={notice?.message ?? ''}
        tone={notice?.tone ?? 'error'}
        onDismiss={() => setNotice(null)}
      />
      <CanonicalCard title="Vista previa para todos los socios" icon={<Eye aria-hidden="true" />}>
        <p>
          Todos los socios elegibles quedan marcados inicialmente. Puedes desmarcarlos y la
          exclusión quedará identificada en el reporte. No crea obligaciones.
        </p>
        <div className="obligation-form-grid">
          <SelectField
            required
            label="Regla"
            value={ruleId}
            onValueChange={setRuleId}
            options={[
              { value: '', label: 'Selecciona una regla' },
              ...rules.map((rule) => ({
                value: rule.id,
                label: `${rule.code} · ${rule.concept.name} · USD ${rule.amount}`,
              })),
            ]}
          />
          <TextField
            required
            label="Período"
            type="month"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          />
        </div>
        <FormActions>
          <Button
            variant="primary"
            disabled={running || !ruleId || !period}
            onClick={() => void run()}
          >
            {running ? 'Calculando…' : 'Calcular vista previa'}
          </Button>
        </FormActions>
      </CanonicalCard>
      {running ? (
        <StatePanel state={canonicalStates.find((state) => state.kind === 'loading')!} />
      ) : null}
      {data ? (
        <>
          {showGuidance ? (
            <CanonicalNotice
              tone="info"
              message="Un socio excluido no se puede incluir desde esta simulación. Usa “Revisar socio” para comprobar su cédula, estado e historial. Si corresponde activarlo, registra el cambio con su motivo y vuelve a calcular la vista previa."
              onDismiss={() => setShowGuidance(false)}
            />
          ) : null}
          <CanonicalMetricGrid>
            <CanonicalMetricCard
              label="Incluidos"
              value={includedRows.length}
              icon={<Users aria-hidden="true" />}
              tone="success"
            />
            <CanonicalMetricCard
              label="Excluidos"
              value={excludedRows.length}
              icon={<UserMinus aria-hidden="true" />}
              tone="warning"
            />
            <CanonicalMetricCard
              label="Exclusiones manuales"
              value={manualExclusions}
              icon={<ListChecks aria-hidden="true" />}
              tone="info"
            />
            <CanonicalMetricCard
              label="Total proyectado"
              value={`$${totalAmount}`}
              icon={<CircleDollarSign aria-hidden="true" />}
              detail="Sin escrituras"
            />
          </CanonicalMetricGrid>
          <Tabs
            label="Filtrar reporte"
            activeId={filter}
            onChange={(id) => {
              setFilter(id as typeof filter);
              setPage(1);
            }}
            items={[
              { id: 'all', label: 'Todos', count: reportRows.length },
              { id: 'included', label: 'Incluidos', count: includedRows.length },
              { id: 'excluded', label: 'Excluidos', count: excludedRows.length },
            ]}
          />
          <section className="obligation-table-card" aria-label="Reporte de obligaciones grupales">
            <DataTable
              paginate={false}
              columns={columns}
              rows={rows}
              visibleColumnIds={new Set(columns.map((column) => column.id))}
              sort={{ columnId: 'member', direction: 'ascending' }}
              onSort={() => {}}
              empty={
                <StatePanel state={canonicalStates.find((state) => state.kind === 'empty')!} />
              }
            />
            <footer>
              <ResultsCounter
                from={rows.length ? (currentPage - 1) * pageSize + 1 : 0}
                to={(currentPage - 1) * pageSize + rows.length}
                total={filteredRows.length}
                noun="socios"
              />
              <Pagination page={currentPage} pages={totalPages} onChange={setPage} />
              <PageSizeSelector
                value={pageSize}
                options={[10, 15, 25, 50]}
                onChange={(value) => {
                  setPageSize(value);
                  setPage(1);
                }}
              />
            </footer>
          </section>
          {canConfirm ? (
            <CanonicalCard title="Confirmación masiva" icon={<ListChecks aria-hidden="true" />}>
              <p>
                La confirmación crea únicamente las obligaciones marcadas. El servidor vuelve a
                validar la regla, los socios y el período antes de escribir.
              </p>
              {confirming ? (
                <ProgressBar
                  value={70}
                  label="Generando obligaciones"
                  detail="Operación idempotente y auditada en curso"
                />
              ) : null}
              <FormActions>
                <Button
                  variant="primary"
                  disabled={confirming || !includedRows.length || Boolean(confirmation)}
                  onClick={() => setConfirmOpen(true)}
                >
                  Confirmar y generar {includedRows.length} obligaciones
                </Button>
              </FormActions>
            </CanonicalCard>
          ) : null}
          {confirmation ? (
            <CanonicalMetricGrid>
              <CanonicalMetricCard
                label="Creadas"
                value={confirmation.createdCount}
                icon={<CircleDollarSign aria-hidden="true" />}
                tone="success"
              />
              <CanonicalMetricCard
                label="Ya existentes"
                value={confirmation.duplicateCount}
                icon={<ListChecks aria-hidden="true" />}
                tone="info"
              />
              <CanonicalMetricCard
                label="Excluidas"
                value={confirmation.excludedCount}
                icon={<UserMinus aria-hidden="true" />}
                tone="warning"
              />
              <CanonicalMetricCard
                label="Total generado"
                value={`$${confirmation.totalAmount}`}
                icon={<CircleDollarSign aria-hidden="true" />}
                detail={`Lote ${confirmation.batchId}`}
              />
            </CanonicalMetricGrid>
          ) : null}
          <ConfirmationDialog
            open={confirmOpen}
            busy={confirming}
            title={`Generar ${includedRows.length} obligaciones`}
            description={`Se crearán obligaciones confirmadas por USD ${totalAmount} para ${data.period}. Las exclusiones permanecerán en el reporte de auditoría.`}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => void confirm()}
          />
        </>
      ) : null}
    </section>
  );
}
